/**
 * CountrySelect - Popover-based searchable dropdown styled to match the standard Shadcn Select.
 *
 * Uses Popover (not Select) so the filter input keeps focus while typing.
 * Radix Select has a built-in typeahead that steals focus from custom inputs.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronsUpDown } from 'lucide-react';
import { adminAPI } from '../services/api';

// Module-level cache - fetched once per session
let _cache = null;
let _promise = null;

export function _bustCountryCache() { _cache = null; _promise = null; }

function fetchCountries() {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = adminAPI.getCountries()
      .then(r => { _cache = r.data.countries || []; return _cache; })
      .catch(() => {
        _promise = null;
        return ['United Kingdom', 'Germany', 'France', 'Ireland', 'Netherlands',
                'United States', 'Canada', 'Australia', 'UAE', 'South Africa',
                'Singapore', 'Other'];
      });
  }
  return _promise;
}

export function CountrySelect({ value, onChange, disabled, placeholder = 'Select country…', testId }) {
  const [countries, setCountries] = useState(_cache || []);
  const [open, setOpen]           = useState(false);
  const [filter, setFilter]       = useState('');
  const inputRef                  = useRef(null);

  useEffect(() => {
    if (!_cache) fetchCountries().then(list => setCountries(list));
  }, []);

  // Auto-focus the filter input whenever the popover opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setFilter('');
  }, [open]);

  const filtered = filter.trim()
    ? countries.filter(c => c.toLowerCase().includes(filter.toLowerCase()))
    : countries;

  const select = (country) => {
    onChange(country);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className="flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm outline-none"
          style={{
            background: 'hsl(var(--background))',
            borderColor: 'hsl(var(--input))',
            color: value ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown size={14} style={{ flexShrink: 0, color: 'hsl(var(--muted-foreground))', marginLeft: 8 }} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)', maxHeight: 280 }}
        align="start"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {/* Filter input - sticky, full focus control, no Radix typeahead interference */}
        <div className="px-2 pt-2 pb-1" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <input
            ref={inputRef}
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Type to filter…"
            className="w-full rounded-md border px-2.5 py-1.5 text-sm outline-none"
            style={{
              background: 'hsl(var(--muted))',
              borderColor: 'hsl(var(--border))',
              color: 'hsl(var(--foreground))',
            }}
          />
        </div>

        {/* Scrollable country list */}
        <div style={{ overflowY: 'auto', maxHeight: 220 }}>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
              No countries match.
            </div>
          )}
          {filtered.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => select(c)}
              className="flex w-full items-center px-3 py-2 text-sm text-left transition-colors"
              style={{
                background: c === value ? 'hsl(var(--accent))' : 'transparent',
                color: 'hsl(var(--foreground))',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { if (c !== value) e.currentTarget.style.background = 'hsl(var(--accent))'; }}
              onMouseLeave={e => { if (c !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {c}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
